import { Injectable } from '@angular/core';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { ViewContext } from 'src/app/shared/components/odata-filter/models/view-context';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import { AdminCredentialsService } from 'src/app/admin/admin-home/admin-credentials/admin-credentials.service';
import { ChildFieldsFilterModel } from 'src/app/shared/odata-filter-models/child-fields-filter-model';
import { SearchValueType } from 'src/app/shared/components/odata-filter/models/search-value-type';
import {
  FilterGroup,
  ODataFilterValues
} from 'src/app/shared/components/odata-filter/models/odata-filter';
import { ODataFilterBuilder } from 'src/app/shared/odata-filter-models/odata-filter-builder';
import { Subject } from 'rxjs';
import { PersonCredentialApplicationReportFilterResult } from './person-credential-application-report.model';
import { PersonCredentialApplicationReportFields } from './person-credential-application-report.fields';
import { CredentialApplicationStatusesReportModel } from '../../models/credential/credential-application-statuses.model';
import { PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS } from '../odata-filter/models/system-filter-models';
import { CredentialDefinitionType } from '../../models/credential/credential-definition.model';

@Injectable({
  providedIn: 'root'
})
export class PersonCredentialApplicationReportService extends BaseODataService {
  private _credentialApplicationDefinitionId: string;
  private _credentialDefinitionId: string;
  public readonly reportConfigured = new Subject<void>();

  constructor(private adminCredentialsService: AdminCredentialsService) {
    super();

    this._filterResult = new PersonCredentialApplicationReportFilterResult();
    this._searchTextModel = PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.SearchFilterModel;
  }
  buildFilter(appliedFilters: SelectedFilter[]) {
    switch (this._viewContext) {
      case ViewContext.PersonCredentialApplicationReport:
        if (this._credentialDefinitionId) {
          this._baseUrl = `PersonCredentialApplicationReport/${this._credentialDefinitionId}`;
        } else {
          this._baseUrl = 'PersonCredentialApplicationReport';
        }
        break;
      default:
        this._baseUrl = 'inProgressPersonCredentialApplication';
    }

    return super.buildFilter(appliedFilters);
  }

  async setApplicationId(
    viewId: string,
    credentialApplicationDefinitionId: string,
    credentialDefinitionId: string,
    viewContext: ViewContext
  ) {
    this._credentialDefinitionId = credentialDefinitionId;
    this._credentialApplicationDefinitionId = credentialApplicationDefinitionId;
    this._viewContext = viewContext;
    this.searchText = '';

    this._filterCollection = [
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.FirstNameFilterModel,
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.LastNameFilterModel,
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.EmailFilterModel,
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationNumberFilterModel,
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationStartDateFilterModel,
      PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationEndDateFilterModel
    ];

    if (this._viewContext == ViewContext.PersonCredentialApplicationReport) {
      this._filterCollection.push(
        PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationStatusFilterModel
      );

      let allApplicationStatusLabels: string[];
      if (credentialDefinitionId) {
        const credentialStatuses =
          await this.adminCredentialsService.getAllStatuses(credentialDefinitionId);
        allApplicationStatusLabels = [
          ...credentialStatuses.applicationStatuses.InProgress,
          ...credentialStatuses.applicationStatuses.Completed,
          ...credentialStatuses.applicationStatuses.Archived
        ].map((s) => s.label);
      } else {
        allApplicationStatusLabels =
          await this.adminCredentialsService.getAllApplicationStatusLabels();
      }
      this.setCredentialApplicationFilter(allApplicationStatusLabels);

      const reportApplicationStatuses =
        await this.adminCredentialsService.getAllApplicationReportStatuses(credentialDefinitionId);
      this.setApplicationStatusesFilter(reportApplicationStatuses);
    }
    if (this._viewContext == ViewContext.PersonCredentialApplicationReport) {
      this._filterCollection.push(
        PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationTypeFilterModel
      );
      const credentials = await this.adminCredentialsService.getAllCredentialsNames(
        CredentialDefinitionType.Person
      );
      if (!credentials || credentials.length == 0) return;
      this.setCredentialNameFilter(credentials);
    } else {
      const applicationDef = await this.adminCredentialsService.getCredentialApplication(
        this._credentialApplicationDefinitionId
      );
      if (applicationDef) {
        const otherFilters = applicationDef.scenarios
          .filter((x) => {
            return x.isActive;
          })
          .map((scenario) => {
            return new ChildFieldsFilterModel(
              PersonCredentialApplicationReportFields.scenarios,
              scenario.name,
              'status',
              SearchValueType.List,
              ['InProgress', 'Completed'],
              ODataFilterBuilder.buildGuidFilter('scenarioDefinitionId', scenario.id),
              FilterGroup.CredentialScenarios
            );
          });
        this._filterCollection.push(...otherFilters);
      }
    }
    this.filterCollection.next(this._filterCollection);
    this.setView(viewId, viewContext);
  }

  private setView(viewId: string, viewContext: ViewContext) {
    this.customViewService.customViews.subscribe((views) => {
      if (!views) return;
      let view = this.customViewService.findView(viewId, viewContext);
      if (!view) {
        view = views.filter((view) => view.isSystemView && view.viewContext === viewContext)[0];
      }
      this.selectView(view);
      this.reportConfigured.next();
    });
  }

  private setCredentialNameFilter(credentials: { id: string; name: string }[]) {
    let model = PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.CredentialNameFilterModel;
    if (this._filterCollection.indexOf(model) === -1) {
      model.values = credentials.map((c) => new ODataFilterValues(c.id, c.name));
      this._filterCollection.splice(3, 0, model);
    }
  }

  private setCredentialApplicationFilter(labels: string[]) {
    const model = PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ApplicationStatusLabelModel;
    if (this._filterCollection.indexOf(model) === -1) {
      model.values = labels.map((label) => new ODataFilterValues(label, label));
      this._filterCollection.push(model);
    }
  }

  private setApplicationStatusesFilter(statuses: CredentialApplicationStatusesReportModel) {
    const filterModels = [
      {
        model: PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.ReinstateApplicationStatusFilterModel,
        values: statuses.reinstateApplicationStatuses
      },
      {
        model: PERSON_CREDENTIAL_APPLICATION_FILTER_MODELS.RenewalApplicationStatusFilterModel,
        values: statuses.renewalApplicationStatuses
      }
    ];

    filterModels.forEach(({ model, values }) => {
      if (this._filterCollection.indexOf(model) === -1) {
        model.values = values.map((s) => new ODataFilterValues(s, s));
        this._filterCollection.push(model);
      }
    });
  }
}
